/* eslint-disable max-len */
import React, { useEffect, useRef } from 'react';
import {
  cloneDeep, compact, get, isEmpty,
} from 'lodash';
import camelCaseDeep from 'camelcase-keys-deep';

import { useRouter } from 'next/router';
import { format, isValid, parse } from 'date-fns';
import { useDispatch } from 'react-redux';
import AddonsBottomPrice from '@/components/AddonsBottomPrice';
import { useGetSessionMutation, useOrderReserveMutation } from '@/slices/gom/apis/orderApi';
import { useAppSelector } from '@/utils/hook';
import {
  aggregatePassengerTypes,
  getDiscountAmount,
  getPriceDetailsOnType,
  getRawTotalTripPrice,
} from '@/utils/tripMapping';
import { formatPhone } from '@/utils/formatPhone';
import {
  resetFlightDiff,
  setFlightPriceDiff,
  setReturnFlightPriceDiff,
  updatePriceForFlight,
  updatePriceForReturnFlight,
} from '@/slices/selectedFlight/slices/selectedFlightSlice';
import { FlightData } from '@/slices/flightData/slices';
import { addFlightToMyBooking } from '@/slices/myBooking/slices/myBookingSlice';
import { BookingFlightStatusEnum } from '@/slices/myBooking/slices/type';
import { resetTimer } from '@/slices/vapi/slices/payMethodSlice';
import TripDetailPrice from '@/containers/TripDetailPrice';
import TripDetailPriceDesktopFixed from '@/containers/Desktop/TripDetailPriceFixedBottom';
import TripDetailPriceDesktop from '@/containers/Desktop/TripDetailPrice';

import {
  addReserveOrderError,
  addReserveResponse,
  setOrderId,
} from '@/slices/global/globalSlice';
import BottomNotification from '../BottomNotification';
import { useTrackBaggaegPage } from '@/utils/hooks/use-gtag';
import { captureException } from '@sentry/nextjs';

interface AddonBaggagesBottomProps {
  onBtnClick?: () => void;
  isPayment?: boolean;
  isDisabled?: boolean;
  isBooking?: boolean;
  isDesktop?: boolean;
}

const AddonBaggagesBottom: React.FC<AddonBaggagesBottomProps> = ({
  onBtnClick,
  isPayment,
  isDisabled,
  isBooking,
  isDesktop,
}) => {
  const { push, pathname } = useRouter();
  const dispatch = useDispatch();

  const {
    flight,
    returnFlight,
    flightPriceDiff,
    returnFlightPriceDiff,
    discountAmount,
    returnDiscountAmount,
  } = useAppSelector((state) => state.selectedFlight);

  const [createOrder, { isLoading, data: responseData }] = useOrderReserveMutation();
  const data = (responseData ? cloneDeep(responseData) : responseData) as any;
  const [getSession, { data: sessionData }] = useGetSessionMutation({
    fixedCacheKey: 'getSession',
  });
  const { asPath } = useRouter();
  const { handleTrack } = useTrackBaggaegPage();

  const { passenger } = useAppSelector((state) => state.searchFlightForm);
  const contactInfo = useAppSelector((state) => state.passenger.contactInfo);
  const selectedPassengerArr = useAppSelector(
    (state) => state.passenger.selectedPassengerArr,
  );
  const reserveResponse = useAppSelector(
    (state) => state.global.reserveResponse,
  );

  const searchFlightForm = useAppSelector((state) => state.searchFlightForm);

  const calendar = useAppSelector((state) => state.calendar);

  const baggage = useAppSelector(
    (state) => state.baggage.selectedBaggages?.depart,
  );
  const returnBaggage = useAppSelector(
    (state) => state.baggage.selectedBaggages?.return,
  );
  const totalBaggage = isEmpty(returnBaggage)
    ? baggage
    : [...baggage, ...returnBaggage];

  const passengerObj = isEmpty(selectedPassengerArr)
    ? passenger
    : aggregatePassengerTypes(selectedPassengerArr);
  const totalBaggagePrice = totalBaggage.reduce(
    (total, currBaggage) => total + currBaggage.baggage.price,
    0,
  );
  if (
    data?.changed?.price_diff
    && Object.keys(data.changed.price_diff)[0].includes('|')
  ) {
    const roundFlightKey = Object.keys(data.changed.price_diff)[0];
    const roundFlightKeyArr = roundFlightKey.split('|');
    roundFlightKeyArr.forEach((flightKey) => {
      (data.changed as any).price_diff[flightKey] = (data.changed as any).price_diff[roundFlightKey]
        / roundFlightKeyArr.length;
    });
  }
  const flightFeeDiff = data?.changed?.price_diff[
    (flight?.startPoint || '') + (flight?.endPoint || '')
  ] || 0;
  const returnFlightFeeDiff = data?.changed?.price_diff[
    (returnFlight?.startPoint || '') + (returnFlight?.endPoint || '')
  ] || 0;

  useEffect(() => {
    if (
      (flightPriceDiff === 0 || flightPriceDiff !== flightFeeDiff)
    && flightFeeDiff !== 0
    ) {
      dispatch(setFlightPriceDiff(flightFeeDiff));
    }
  }, [flightFeeDiff]);
  useEffect(() => {
    if (
      (returnFlightPriceDiff === 0
      || returnFlightPriceDiff !== returnFlightFeeDiff)
    && returnFlightFeeDiff !== 0
    ) {
      dispatch(setReturnFlightPriceDiff(returnFlightFeeDiff));
    }
  }, [returnFlightFeeDiff]);

  const totalPrice = getRawTotalTripPrice(flight, passengerObj);
  const totalReturnPrice = getRawTotalTripPrice(returnFlight, passengerObj);
  const departDiscount = getDiscountAmount(flight, passengerObj);
  const returnDiscount = getDiscountAmount(returnFlight, passengerObj);
  const totalChangedPrice = totalPrice + flightPriceDiff;
  const totalChangedReturnPrice = totalReturnPrice + returnFlightPriceDiff;
  const totalAllPrice = totalChangedPrice + totalChangedReturnPrice;

  const oldPrice = totalPrice + totalReturnPrice + totalBaggagePrice - ((discountAmount || departDiscount || 0) + (returnDiscountAmount || returnDiscount || 0));
  const newPrice = totalAllPrice + totalBaggagePrice - ((discountAmount || departDiscount || 0) + (returnDiscountAmount || returnDiscount || 0));

  const priceDiffRef = useRef({
    oldPrice,
    newPrice,
  });

  useEffect(() => {
    priceDiffRef.current = {
      oldPrice,
      newPrice,
    };
  }, [
    oldPrice,
    newPrice,
  ]);
  const extractPriceDiffResponse = (response: any) => {
    try {
      if (Object.keys(response.changed.price_diff)[0].includes('|')) {
        const roundFlightKey = Object.keys(response.changed.price_diff)[0];
        const roundFlightKeyArr = roundFlightKey.split('|');
        roundFlightKeyArr.forEach((flightKey) => {
          (response.changed as any).price_diff[flightKey] = (response.changed as any).price_diff[roundFlightKey]
            / roundFlightKeyArr.length;
        });
      }
      const diff = (response?.changed?.price_diff[
        (flight?.startPoint || '') + (flight?.endPoint || '')
      ] || 0)
        + (response?.changed?.price_diff[
          (returnFlight?.startPoint || '') + (returnFlight?.endPoint || '')
        ] || 0);
      const { oldPrice: oldPriceUpdated, newPrice: newPriceUpdated } = priceDiffRef.current;
      const error = {
        priceDiff: {
          oldPrice: oldPriceUpdated,
          newPrice: newPriceUpdated + diff,
        },
      };
      return error;
    } catch (err) {
      captureException(err);
    }
    return null;
  };

  const bookingBtnClick = () => {
    onReserveOrder();
    handleTrack();
  };

  const onReserveOrder = (disableRedirect?: boolean) => {
    const flights: FlightData[] = [];
    if (flight) {
      flights.push({
        ...flight,
        priceDiff: flightPriceDiff,
      });
    }
    if (returnFlight) {
      flights.push({
        ...returnFlight,
        priceDiff: returnFlightPriceDiff,
      });
    }

    // dispatch(resetFlightDiff());
    dispatch(setOrderId({ orderId: '' }));
    createOrder({
      contact: {
        address: '',
        email: contactInfo?.email.toLowerCase(),
        first_name: contactInfo?.firstName,
        last_name: contactInfo?.lastName,
        phone: formatPhone(contactInfo?.phone || ''),
      },
      list_passenger: selectedPassengerArr?.map((x, index) => {
        const birthDay = x.birthday
          ? parse(x.birthday, 'dd/MM/yyyy', new Date())
          : new Date(x.birthday);
        return {
          birthday: format(
            isValid(birthDay) ? birthDay : new Date(x.birthday),
            'yyyy-MM-dd',
          ),
          first_name: x?.firstName,
          gender: Number(x?.gender),
          index,
          last_name: x?.lastName,
          list_baggage: totalBaggage
            .filter((bag) => bag.contactId === x.id)
            .map((z) => ({
              airline: z?.baggage?.airline,
              leg: z?.baggage?.leg,
              route: z?.baggage?.route,
              code: z?.baggage?.code,
              currency: z?.baggage?.currency,
              name: z?.baggage?.name,
              price: z?.baggage?.price,
              value: z?.baggage?.valueOriginal || z?.baggage?.value,
              payload_book_baggage: z?.baggage?.payloadBookBaggage,
            })),
          type: x ? x.type.toUpperCase() : 'ADT',
          list_price_detail: flights.map((currFlight) => getPriceDetailsOnType(currFlight, x, selectedPassengerArr.length)),
        };
      }),
      list_flight: compact([
        {
          session: sessionData ? sessionData?.departSession?.session : flight?.session,
          leg: 0,
          flight_id: flight?.idIndex,
          flight_value: flight?.route.schedules[0].flightValue,
          airline: flight?.airline,
          operation: flight?.route?.schedules[0]?.operating,
          start_point: flight?.startPoint,
          end_point: flight?.endPoint,
          depart_date: format(
            parse(flight?.date || '', 'yyyy-MM-dd', new Date()),
            'ddMMyyyy',
          ),
          depart_time: (flight?.time || '').replace(':', ''),
          flight_number: flight?.flightNumber,
          fare_class: get(flight?.route?.schedules, '[0].fareClass'),
          price: totalChangedPrice || totalPrice,
          fare_data_id: flight?.fareDataId,
          group_class: flight?.segments?.[0]?.cabin,
          provider: flight?.provider,
          payload_book_flight: flight?.payloadBookFlight,
          payload_search_baggage: flight?.payloadSearchBaggage,

        },
        returnFlight && {
          session: sessionData ? sessionData?.returnSession?.session : returnFlight?.session,
          leg: 1,
          flight_id: returnFlight?.idIndex,
          flight_value: returnFlight?.route.schedules[0].flightValue,
          airline: returnFlight?.airline,
          operation: returnFlight?.route?.schedules[0]?.operating,
          start_point: returnFlight?.startPoint,
          end_point: returnFlight?.endPoint,
          depart_date: format(
            parse(returnFlight?.date || '', 'yyyy-MM-dd', new Date()),
            'ddMMyyyy',
          ),
          depart_time: (returnFlight?.time || '').replace(':', ''),
          flight_number: returnFlight?.flightNumber,
          fare_class: get(returnFlight?.route?.schedules, '[0].fareClass'),
          price: totalChangedReturnPrice || totalReturnPrice,
          fare_data_id: returnFlight?.fareDataId,
          group_class: returnFlight?.segments?.[0]?.cabin,
          provider: returnFlight?.provider,
          payload_book_flight: returnFlight?.payloadBookFlight,
          payload_search_baggage: returnFlight?.payloadSearchBaggage,
        },
      ]),
      session: sessionData,
    })
      .unwrap()
      .then(async (responseData2) => {
        const response = cloneDeep(responseData2);
        if (response?.changed?.price_diff) {
          const error = extractPriceDiffResponse(response);
          if (error) {
            dispatch(addReserveOrderError({ error }));
          }
        } else if (!response.orders || response.orders.length === 0) {
          throw new Error('Không có thông tin chuyến, không thể đặt chỗ');
        }
        if (response?.list_fare_data) {
          response.list_fare_data.forEach((fareData) => {
            const { route } = fareData;
            if (flight && flight?.startPoint + flight?.endPoint === route) {
              dispatch(updatePriceForFlight(camelCaseDeep(fareData) as any));
            } else if (
              returnFlight
              && returnFlight.startPoint + returnFlight.endPoint === route
            ) {
              dispatch(updatePriceForReturnFlight(camelCaseDeep(fareData)));
            }
          });
        }
        dispatch(addReserveResponse({ response }));
      })
      .catch((responseError) => {
        const responseErrorData = responseError.data || {};
        let errorData = {} as any;
        if (!isEmpty(responseErrorData.data)) {
          errorData = responseErrorData.data;
        } else {
          try {
            const errorArr = JSON.parse(responseErrorData.message);
            errorData.message = JSON.parse(errorArr[0]).message;
          } catch (err) {
            captureException(err);
            errorData.message = responseErrorData.message || responseError.message;
          }
        }
        if (errorData) {
          dispatch(
            addReserveOrderError({
              error: {
                ...errorData,
                flightId: flight?.idIndex,
                from: flight?.startPoint,
                to: flight?.endPoint,
                date: flight?.date,
                airline: flight?.airline,
              },
            }),
          );
        }
      });

    if (!disableRedirect && !asPath.includes('trip-detail')) {
      const preOrderTotalPrice = totalAllPrice + totalBaggagePrice;
      push({
        pathname: '/pay',
        query: {
          phone: get(contactInfo, 'phone'),
          email: get(contactInfo, 'email')?.toLowerCase(),
          value: preOrderTotalPrice,
          code: '',
          id: '',
        },
      });
    }
    dispatch(resetTimer());
  };

  useEffect(() => {
    if (!isEmpty(reserveResponse?.orders)) {
      dispatch(
        addFlightToMyBooking({
          id: get(reserveResponse, 'orders[0].id'),
          flightsData: {
            flight,
            returnFlight,
            flightPriceDiff,
            returnFlightPriceDiff,
            discountAmount,
            returnDiscountAmount,
          },
          bookingInfo: {
            status: BookingFlightStatusEnum.PENDING,
            timeReserveTicket: new Date().toISOString(),
            timeExpireTicket: get(reserveResponse, 'orders[0].expireTime'),
          },
          bookingCode: get(reserveResponse, 'orders[0].code'),
          paymentQueryPages: {
            phone: get(reserveResponse, 'orders[0].contactPhone'),
            email: get(reserveResponse, 'orders[0].contactEmail'),
            value: get(reserveResponse, 'orders[0].amount'),
            code: get(reserveResponse, 'orders[0].code'),
            id: get(reserveResponse, 'orders[0].id'),
          },
          searchFlightForm,
          calendar,
          selectedBaggages: {
            depart: baggage,
            return: returnBaggage,
          },
          selectedPassengerArr,
        }),
      );
    }
  }, [
    reserveResponse,
    flight,
    returnFlight,
    flightPriceDiff,
    returnFlightPriceDiff,
    discountAmount,
    returnDiscountAmount,
  ]);

  return (
    <>
      {isDesktop && pathname !== '/pay/desktop' ? (
        <TripDetailPriceDesktop
          onBtnClick={isBooking ? bookingBtnClick : onBtnClick}
          isPayment={isPayment}
          isDisabled={isDisabled}
        />
      ) : (isDesktop && pathname === '/pay/desktop') ? (
        <TripDetailPriceDesktopFixed
          isLoadingProps={isLoading}
          isPayment={isPayment}
          isDisabled={isDisabled}
          onBtnClick={isBooking ? bookingBtnClick : onBtnClick}
        />
      ) : isBooking ? (
        <AddonsBottomPrice
          isLoading={isLoading}
          title="Phí hành lý mua thêm"
          onBtnClick={() => { onReserveOrder(); handleTrack(); }}
        />
      ) : (
        <TripDetailPrice
          onBtnClick={onBtnClick}
          isPayment={isPayment}
          isDisabled={isDisabled}
        />
      )}
      <BottomNotification onReserveOrder={onReserveOrder} />
    </>
  );
};

export default AddonBaggagesBottom;
